import type {
  CreatePersonCommandData,
  PersonCreationDataV2,
} from '@festina/legalentity-general-types/legalentity'
import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useNavigate } from 'react-router'

import { useVariantAction } from '../../hooks/useVariantAction'
import { useHook } from '../../lenteHooks'
import { generalRoutes } from '../../routing/generalRoutes'
import { CommandModalButton } from '../commandActions/CommandModalButton'
import type { ActionButtonProps } from '../commandActions/types'
import { CreatePersonForm } from './CreatePersonForm'

export function CreatePersonCommand(props: ActionButtonProps) {
  const translate = useTranslate()
  const navigate = useNavigate()

  const tenantId = useHook('routing.tenant.useTenantParam')()
  const { ownerId } = useHook('routing.general.useEntityParams')({
    isOptional: true,
  })
  const actions = useHook('mutators.crm.usePersonCreationActions')({
    tenantId,
    personId: ownerId ?? '',
    ...props,
  })
  const { mutateAsync: handleFetchInternalId } = useHook(
    'lazyFetchers.crm.useLazyFetchInternalId',
  )()

  const actionsOverwrite = {
    ...actions,
    // As our API packages does not know of general the navigation has to be done here
    // Basically what it does it makes a look up right after the user has been created, and then navigated.
    // This previously did not work, but works now and the backend (Contact: Mads Lindstrøn)
    ...(actions.create
      ? {
          create: {
            ...actions.create,
            mutateAsync: async (
              data: {
                identificationCreationData?: PersonCreationDataV2['identificationCreationData']
              } & CreatePersonCommandData,
            ) => {
              await actions.create?.mutateAsync(data)

              assertNotEmpty(data.identificationCreationData?.externalId)

              const internalId = await handleFetchInternalId({
                externalId: data.identificationCreationData.externalId,
                tenantId,
              })

              assertNotEmpty(internalId)

              await navigate(
                generalRoutes.person.legalEntity({
                  tenantId,
                  ownerId: internalId,
                }),
              )
            },
          },
        }
      : undefined),
  }
  const submit = useVariantAction({ ...props, actions: actionsOverwrite })

  return (
    <CommandModalButton
      actionId="legalEntity.CreatePersonCommandData"
      title={translate('createLegalEntityModalButton.button')}
      commandDataForm={CreatePersonForm}
      {...props}
      submit={submit}
    />
  )
}
